import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { setBookmark } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

type BookmarkButtonProps = {
  slug: string;
  bookmarked: boolean; // initial saved state from the skill payload
  size?: "sm" | "md";
  className?: string;
  onChange?: (bookmarked: boolean) => void; // notify parent (e.g. to drop from a list)
  matchTitle?: boolean; // when unsaved, render at full title brightness (skill-page header)
};

const ICON = { sm: 18, md: 21 };

// Save/unsave toggle. Optimistic: flips instantly, reverts on error. A logged-out
// click routes to /login?next=<here> (the same pattern X uses) rather than failing.
export default function BookmarkButton({
  slug,
  bookmarked,
  size = "sm",
  className,
  onChange,
  matchTitle = false,
}: BookmarkButtonProps) {
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [saved, setSaved] = useState(bookmarked);
  const [busy, setBusy] = useState(false);

  // Stay in sync when the parent re-fetches with a fresh state.
  useEffect(() => setSaved(bookmarked), [bookmarked]);

  async function toggle(e: React.MouseEvent) {
    // The card is a link — don't navigate when the icon is clicked.
    e.preventDefault();
    e.stopPropagation();

    if (!user || !accessToken) {
      const next = location.pathname + location.search;
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return;
    }
    if (busy) return;

    const nextSaved = !saved;
    setSaved(nextSaved); // optimistic
    setBusy(true);
    onChange?.(nextSaved);
    try {
      await setBookmark(slug, nextSaved, accessToken);
    } catch {
      setSaved(!nextSaved); // revert on failure
      onChange?.(!nextSaved);
    } finally {
      setBusy(false);
    }
  }

  const px = ICON[size];
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={saved}
      aria-label={saved ? "Remove bookmark" : "Bookmark this skill"}
      title={saved ? "Bookmarked" : "Bookmark"}
      className={`flex shrink-0 items-center justify-center rounded-md p-1 transition-colors ${
        saved ? "text-primary" : matchTitle ? "text-text hover:text-text/80" : "text-text/35 hover:text-text/70"
      } ${className ?? ""}`}
    >
      <svg
        width={px}
        height={px}
        viewBox="0 0 24 24"
        fill={saved ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  );
}
